import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import "./VideoPlayer.scss";

export default function VideoPlayer({
  videos = [],
  heading,
  subheading,
  className = "",
}) {
  const playerRefs = useRef({});
  const clickHandlers = useRef({});
  const [portraitMap, setPortraitMap] = useState({});
  const [playingId, setPlayingId] = useState(null);

  const onPlayerPlay = (id) => {
    setPlayingId(id);
  };

  const onPlayerPause = (id) => {
    setPlayingId((prev) => (prev === id ? null : prev));
  };

  const handleCardClick = (id) => {
    setPlayingId((prev) => (prev === id ? null : id));
  };

  const handleInnerClick = (e, id) => {
    try {
      const r = playerRefs.current[id];
      let internal = null;
      try {
        internal = r && r.getInternalPlayer ? r.getInternalPlayer() : null;
      } catch (err) {
        internal = null;
      }

      const videoEl =
        internal &&
        (internal.nodeName === "VIDEO"
          ? internal
          : internal && internal.querySelector
            ? internal.querySelector("video")
            : null);

      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        null;
      const isFs =
        fsEl &&
        videoEl &&
        (fsEl === videoEl || (fsEl.contains && fsEl.contains(videoEl)));

      if (!isFs) {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        const bottomArea = 80;
        if (rect.height - clickY < bottomArea) {
          return;
        }
      } else {
        const tg = e.target;
        if (tg && tg.tagName) {
          const tag = tg.tagName.toUpperCase();
          if (["BUTTON", "INPUT", "A", "SELECT", "TEXTAREA"].includes(tag)) {
            return;
          }
        }
      }

      e.preventDefault();
      e.stopPropagation();

      setPlayingId((prev) => {
        const next = prev === id ? null : id;

        if (videoEl) {
          try {
            if (next === id) {
              videoEl.play().catch(() => {});
            } else {
              try {
                videoEl.pause();
              } catch (err) {}
            }
          } catch (err) {}
        }

        return next;
      });
    } catch (err) {}
  };

  const onPlayerReady = (id) => {
    const playerWrapper = playerRefs.current[id];
    if (!playerWrapper || !playerWrapper.getInternalPlayer) return;
    const internal = playerWrapper.getInternalPlayer();
    if (!internal) return;

    try {
      const w = internal.videoWidth || internal.clientWidth || 0;
      const h = internal.videoHeight || internal.clientHeight || 0;
      const isPortrait = h > w;
      setPortraitMap((prev) => ({ ...prev, [id]: isPortrait }));
    } catch (e) {}

    const videoEl =
      internal.nodeName === "VIDEO"
        ? internal
        : internal.querySelector
          ? internal.querySelector("video")
          : null;
    if (!videoEl) return;

    if (clickHandlers.current[id]) {
      try {
        videoEl.removeEventListener("click", clickHandlers.current[id]);
      } catch (e) {}
      clickHandlers.current[id] = null;
    }

    const handler = (e) => {
      try {
        const rect = videoEl.getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        const bottomArea = 80;
        if (rect.height - clickY < bottomArea) {
          return;
        }

        if (videoEl.paused) {
          videoEl.play();
          setPlayingId(id);
        } else {
          videoEl.pause();
          setPlayingId((prev) => (prev === id ? null : prev));
        }
      } catch (err) {}
    };

    videoEl.addEventListener("click", handler);
    clickHandlers.current[id] = handler;
  };

  useEffect(() => {
    const clickHandlersLocal = clickHandlers.current;
    const playerRefsLocal = playerRefs.current;

    const onFsChange = () => {
      try {
        const fsEl =
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement ||
          null;
        Object.keys(playerRefsLocal).forEach((id) => {
          const r = playerRefsLocal[id];
          if (!r || !r.getInternalPlayer) return;
          const internal = r.getInternalPlayer();
          const videoEl =
            internal &&
            (internal.nodeName === "VIDEO"
              ? internal
              : internal.querySelector
                ? internal.querySelector("video")
                : null);
          if (!videoEl) return;

          const isFs =
            fsEl === videoEl ||
            (fsEl && fsEl.contains && fsEl.contains(videoEl));
          if (isFs) {
            videoEl.style.objectFit = portraitMap[id] ? "contain" : "cover";
          } else {
            videoEl.style.objectFit = "cover";
          }
        });
      } catch (e) {}
    };

    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("mozfullscreenchange", onFsChange);
    document.addEventListener("MSFullscreenChange", onFsChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("mozfullscreenchange", onFsChange);
      document.removeEventListener("MSFullscreenChange", onFsChange);

      try {
        Object.keys(clickHandlersLocal).forEach((id) => {
          const handler = clickHandlersLocal[id];
          const r = playerRefsLocal[id];
          if (!handler || !r || !r.getInternalPlayer) return;
          const internal = r.getInternalPlayer();
          const videoEl =
            internal &&
            (internal.nodeName === "VIDEO"
              ? internal
              : internal.querySelector
                ? internal.querySelector("video")
                : null);
          if (videoEl && handler) {
            try {
              videoEl.removeEventListener("click", handler);
            } catch (e) {}
          }
        });
      } catch (e) {}
    };
  }, [portraitMap]);

  return (
    <div className={`video-player ${className}`.trim()}>
      {(heading || subheading) && (
        <div className="video-player-header">
          {heading && <h2 className="video-player-heading">{heading}</h2>}
          {subheading && (
            <p className="video-player-subheading">{subheading}</p>
          )}
        </div>
      )}

      <div className="video-cards">
        {videos.map((v) => (
          <div
            key={v.id}
            role="button"
            tabIndex={0}
            className="video-card"
            onClick={() => handleCardClick(v.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCardClick(v.id);
            }}
          >
            <div className="thumb">
              <div
                style={{ position: "relative", width: "100%", height: "100%" }}
                onClickCapture={(e) => handleInnerClick(e, v.id)}
              >
                <ReactPlayer
                  ref={(r) => (playerRefs.current[v.id] = r)}
                  url={v.src}
                  playing={playingId === v.id}
                  controls
                  width="100%"
                  height="100%"
                  onReady={() => onPlayerReady(v.id)}
                  onPlay={() => onPlayerPlay(v.id)}
                  onPause={() => onPlayerPause(v.id)}
                  config={{ file: { attributes: { playsInline: true } } }}
                  style={{ objectFit: "cover" }}
                />

                {playingId !== v.id && <div className="play-overlay">▶</div>}
              </div>
            </div>

            {v.title || v.description ? (
              <div className="meta">
                {v.title && <div className="meta-title">{v.title}</div>}
                {v.description && (
                  <div className="meta-desc">{v.description}</div>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
